import React, { useEffect, useRef, useState } from 'react';
import '../../css/visualComponent/OrderForm.css';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Turnstile } from '@marsidev/react-turnstile';
import { orderSchema, SHAPES, COLORS, FLAVORS } from '../../lib/orderSchema';
import { useConfigurator } from '../../services/ConfiguratorContextProvider';

const DEFAULTS = {
  customerName: '',
  phoneNumber: '',
  company: '',
  email: '',
  address: '',
  quantity: 1,
  shape: 'oval',
  size: 10,
  color: 'natural',
  flavor: 'natural',
  message: '',
  website: '',
  cfTurnstileToken: '',
};

const ENUM_FIELDS = new Set(['shape', 'color', 'flavor']);
const NUMERIC_FIELDS = new Set(['quantity', 'size']);

// Map raw zod error codes (set by @hookform/resolvers/zod) to our i18n keys.
// Server already returns normalized keys; this function is idempotent for those.
function normalizeErrorType(field, type) {
  if (!type) return 'invalid';
  // Already normalized (from server 400 response)
  if (['required', 'minLength', 'invalidEmail', 'outOfRange', 'invalidEnum',
       'captcha', 'rateLimit', 'generic', 'network', 'invalid'].includes(type)) {
    return type;
  }
  // Email-specific
  if (field === 'email') {
    if (type === 'invalid_type' || type === 'too_small') return 'required';
    return 'invalidEmail';
  }
  // Enum fields
  if (ENUM_FIELDS.has(field)) {
    if (type === 'invalid_enum_value') return 'invalidEnum';
    return 'required';
  }
  // Numeric fields
  if (NUMERIC_FIELDS.has(field)) {
    if (type === 'invalid_type') return 'required';
    return 'outOfRange';
  }
  // String fields
  if (type === 'invalid_type') return 'required';
  if (type === 'too_small') return 'minLength';
  if (type === 'too_big') return 'outOfRange';
  if (type === 'invalid_literal') return 'invalid';
  return 'invalid';
}

const Field = ({ id, label, error, errorKey, full = false, children }) => {
  const cls = [
    'form-field',
    full && 'form-field--full',
    error && 'form-field--error',
  ].filter(Boolean).join(' ');
  return (
    <div className={cls}>
      <label htmlFor={id} className="form-field-label">{label}</label>
      {children}
      {error && (
        <div className="form-field-tooltip" role="alert" key={errorKey}>
          {error}
        </div>
      )}
    </div>
  );
};

export const OrderForm = () => {
  const { t } = useTranslation('global');
  const turnstileRef = useRef(null);
  const [okMessage, setOkMessage] = useState(false);
  const [errorBanner, setErrorBanner] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    getValues,
    formState: { errors, isValid, submitCount },
  } = useForm({
    resolver: zodResolver(orderSchema),
    mode: 'onBlur',
    defaultValues: DEFAULTS,
  });

  const {
    committedAt,
    consumeCommittedSnapshot,
    reset: resetConfig,
  } = useConfigurator();

  useEffect(() => {
    if (committedAt === null) return;
    const snapshot = consumeCommittedSnapshot();
    if (!snapshot) return;
    reset({
      ...DEFAULTS,
      ...getValues(),
      ...snapshot,
    });
  }, [committedAt, consumeCommittedSnapshot, reset, getValues]);

  const turnstileToken = watch('cfTurnstileToken');
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  // Tooltips auto-hide via CSS animation (3s). Re-mounting the tooltip element
  // (via React key based on submitCount) re-triggers the animation per submit.

  const onSubmit = async (data) => {
    setSubmitting(true);
    setErrorBanner(null);
    setOkMessage(false);
    try {
      const res = await fetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (res.status === 200 || res.status === 204) {
        reset(DEFAULTS);
        setOkMessage(true);
        turnstileRef.current?.reset?.();
        resetConfig();
        return;
      }
      if (res.status === 400) {
        const body = await res.json();
        Object.entries(body.fields || {}).forEach(([k, v]) =>
          setError(k, { type: v, message: t(`order_form.errors.${v}`) }),
        );
        return;
      }
      if (res.status === 403) {
        setErrorBanner('captcha');
        turnstileRef.current?.reset?.();
        setValue('cfTurnstileToken', '');
        return;
      }
      if (res.status === 429) {
        setErrorBanner('rateLimit');
        return;
      }
      setErrorBanner('generic');
    } catch {
      setErrorBanner('network');
    } finally {
      setSubmitting(false);
    }
  };

  const errMsg = (field) => {
    const e = errors[field];
    if (!e) return null;
    const key = normalizeErrorType(field, e.type);
    return t(`order_form.errors.${key}`);
  };

  return (
    <form
      className="contact-section-form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
    >
      <div className="form-grid">
        <Field id="customerName" label={t('order_form.form.customerName')} error={errMsg('customerName')} errorKey={submitCount}>
          <input id="customerName" type="text" {...register('customerName')} />
        </Field>

        <Field id="phoneNumber" label={t('order_form.form.phone')} error={errMsg('phoneNumber')} errorKey={submitCount}>
          <input id="phoneNumber" type="text" {...register('phoneNumber')} />
        </Field>

        <Field id="company" label={t('order_form.form.company')} error={errMsg('company')} errorKey={submitCount}>
          <input id="company" type="text" {...register('company')} />
        </Field>

        <Field id="email" label={t('order_form.form.email')} error={errMsg('email')} errorKey={submitCount}>
          <input id="email" type="email" {...register('email')} />
        </Field>

        <Field id="address" label={t('order_form.form.address')} error={errMsg('address')} errorKey={submitCount}>
          <input id="address" type="text" {...register('address')} />
        </Field>

        <Field id="quantity" label={t('order_form.form.quantity')} error={errMsg('quantity')} errorKey={submitCount}>
          <input id="quantity" type="number" min="1" step="1" {...register('quantity', { valueAsNumber: true })} />
        </Field>

        <Field id="shape" label={t('order_form.form.shape.label')} error={errMsg('shape')} errorKey={submitCount}>
          <select id="shape" {...register('shape')}>
            {SHAPES.map((s) => (
              <option key={s} value={s}>
                {t(`order_form.form.shape.options.${s}`)}
              </option>
            ))}
          </select>
        </Field>

        <Field id="size" label={t('order_form.form.size.label')} error={errMsg('size')} errorKey={submitCount}>
          <input id="size" type="number" min="1" max="30" step="0.1" {...register('size', { valueAsNumber: true })} />
        </Field>

        <Field id="color" label={t('order_form.form.color.label')} error={errMsg('color')} errorKey={submitCount}>
          <select id="color" {...register('color')}>
            {COLORS.map((c) => (
              <option key={c} value={c}>
                {t(`order_form.form.color.options.${c}`)}
              </option>
            ))}
          </select>
        </Field>

        <Field id="flavor" label={t('order_form.form.flavor.label')} error={errMsg('flavor')} errorKey={submitCount}>
          <select id="flavor" {...register('flavor')}>
            {FLAVORS.map((f) => (
              <option key={f} value={f}>
                {t(`order_form.form.flavor.options.${f}`)}
              </option>
            ))}
          </select>
        </Field>

        <Field id="message" label={t('order_form.form.message')} error={errMsg('message')} errorKey={submitCount} full>
          <textarea id="message" rows="5" {...register('message')} />
        </Field>
      </div>

      {/* honeypot — hidden from humans */}
      <input
        type="text"
        tabIndex="-1"
        autoComplete="off"
        className="hp-trap"
        {...register('website')}
      />

      {siteKey ? (
        <div className="form-turnstile">
          <Turnstile
            ref={turnstileRef}
            siteKey={siteKey}
            onSuccess={(token) =>
              setValue('cfTurnstileToken', token, { shouldValidate: true })
            }
            onExpire={() => setValue('cfTurnstileToken', '')}
            onError={() => setValue('cfTurnstileToken', '')}
          />
        </div>
      ) : null}

      <button
        type="submit"
        className="form-submit"
        disabled={submitting || !isValid || !turnstileToken}
      >
        {t('order_form.form.submit')}
      </button>

      {okMessage && (
        <p className="form-success">
          {t('order_form.success')}
        </p>
      )}
      {errorBanner && (
        <p className="form-banner-error">
          {t(`order_form.errors.${errorBanner}`)}
        </p>
      )}
    </form>
  );
};
